import { type ConflictDto, type PersonDto, type TaskDto, type TeamDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, List, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useState } from 'react';
import { IconAlertTriangle, IconCarOff } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { dayLong, dayShort } from '../../format';
import { minHint } from '../../minHint';

export const usePeople = () => useQuery({ queryKey: ['people'], queryFn: () => api<PersonDto[]>('/people'), staleTime: 60_000 });
export const useTeams = () => useQuery({ queryKey: ['teams'], queryFn: () => api<TeamDto[]>('/teams') });

const WD = ['nd', 'pn', 'wt', 'śr', 'cz', 'pt', 'sb'];
export const dayLabel = (d: string) => {
  const dt = new Date(`${d}T12:00:00Z`);
  return { wd: WD[dt.getUTCDay()]!, dm: dayShort(d), weekend: [0, 6].includes(dt.getUTCDay()) };
};
/** „czwartek, 5 marca” — dayLong bez roku. */
export const longDay = (d: string) => dayLong(d, { year: false });

export const termText = (t: Pick<TaskDto, 'dateFrom' | 'dateTo' | 'timeFrom' | 'timeTo'>) => {
  if (!t.dateFrom) return 'bez terminu';
  const days = t.dateFrom === t.dateTo ? dayLabel(t.dateFrom).dm : `${dayLabel(t.dateFrom).dm}–${dayLabel(t.dateTo!).dm}`;
  return `${days}${t.timeFrom ? `, ${t.timeFrom}–${t.timeTo}` : ', cały dzień'}`;
};

const SEV_COLOR = { wysoka: 'red', srednia: 'orange', niska: 'yellow' } as const;

export function ConflictList({ conflicts, max = 8 }: { conflicts: ConflictDto[]; max?: number }) {
  if (!conflicts.length) return null;
  return (
    <List size="sm" spacing={2} icon={<IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />}>
      {conflicts.slice(0, max).map((c, i) => (
        <List.Item key={i}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm">{c.text}</Text>
            <Badge size="xs" color={SEV_COLOR[c.severity]} variant="light">
              {c.severity === 'wysoka' ? 'konflikt' : c.severity === 'srednia' ? 'wydarzenie' : 'wniosek'}
            </Badge>
          </Group>
        </List.Item>
      ))}
      {conflicts.length > max && <List.Item>… i {conflicts.length - max} więcej</List.Item>}
    </List>
  );
}

/** Czerwony trójkąt z opisem konfliktów (najechanie). */
export function ConflictMark({ conflicts }: { conflicts: ConflictDto[] }) {
  if (!conflicts.length) return null;
  return (
    <Tooltip label={conflicts.slice(0, 6).map((c) => c.text).join('\n')} multiline w={320} style={{ whiteSpace: 'pre-line' }}>
      <IconAlertTriangle size={14} color={conflicts.some((c) => c.severity === 'wysoka') ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-orange-6)'} style={{ flexShrink: 0 }} />
    </Tooltip>
  );
}

/** Okno z prośbą o powód (zamiast okna przeglądarki). */
export function ReasonDialog({ title, label, confirm, color, onConfirm, onClose, busy }: { title: string; label: string; confirm: string; color?: string; busy?: boolean; onConfirm: (reason: string) => void; onClose: () => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal opened onClose={onClose} title={title} size="sm">
      <Stack>
        <TextInput label={label} required description={minHint(reason)} value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {/* gdy główna akcja sama brzmi „Anuluj …”, drugi przycisk to „Wróć” */}
            {confirm.startsWith('Anuluj') ? 'Wróć' : 'Anuluj'}
          </Button>
          <Button color={color} disabled={reason.trim().length < 3} loading={busy} onClick={() => onConfirm(reason.trim())}>
            {confirm}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}


/** Flota (docs/19 §6): pomarańczowe auto z opisem niedostępności (najechanie) — informacja, bez blokady. */
export function FleetMark({ warnings, size = 14 }: { warnings: string[] | undefined; size?: number }) {
  if (!warnings?.length) return null;
  return (
    <Tooltip label={warnings.slice(0, 6).join('\n')} multiline w={320} style={{ whiteSpace: 'pre-line' }}>
      <IconCarOff size={size} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} aria-label="Auto niedostępne" />
    </Tooltip>
  );
}

/** Flota (docs/19 §6): ramka „Auto niedostępne” na karcie zadania i przy planowaniu. */
export function FleetWarnings({ warnings, note = 'Nie blokuje planowania — może być auto zastępcze.' }: { warnings: string[] | undefined; note?: string }) {
  if (!warnings?.length) return null;
  return (
    <Alert color="orange" variant="light" icon={<IconCarOff size={18} />} title={warnings.length === 1 ? 'Auto niedostępne' : 'Auta niedostępne'}>
      <Stack gap={2}>
        {warnings.map((w) => (
          <Text key={w} size="sm">
            {w}
          </Text>
        ))}
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      </Stack>
    </Alert>
  );
}
